/* eslint-disable no-param-reassign */
import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  makeTree,
  setField,
  DEFAULT_SAVED_SEARCH_TREE_SORT,
  getSavedSearchGroupDeleteImpactCounts,
} from '../helpers/savedSearches';

/**
 * Same timing as `setSavedSearchRemoving` / collections remove: mark the group
 * row busy only after confirmations and while the delete request is in flight.
 * `path` matches virtual group `node._id` / `setField` lookup in the tree.
 */
export const setSavedSearchGroupRemoving = createAction(
  'savedSearches/setGroupRemoving',
  ({ path, isRemoving }) => ({ payload: { path, isRemoving } }),
);

export const deleteGroup = createAsyncThunk(
  'savedSearches/deleteGroup',
  async (
    { path, isShared, showDialog = true },
    {
      extra: {
        sdk, Logger, Toast, showDialogAsync, localization: l10n,
      },
      getState,
      dispatch,
    },
  ) => {
    try {
      const groupName = path.split(NEW_PATH_DELIMITER).pop();
      const { all } = getState().savedSearches;
      const { savedSearchCount, subgroupCount } = getSavedSearchGroupDeleteImpactCounts({
        all,
        path,
        isShared,
      });
      if (showDialog) {
        try {
          const { promise } = showDialogAsync({
            title: l10n.TAGSTREE.titleDeleteSavedSearchGroupWithContents(groupName),
            text: l10n.TAGSTREE.textDeleteSavedSearchGroupWithContents(
              savedSearchCount,
              subgroupCount,
            ),
            textBtnOk: l10n.TAGSTREE.btnDeleteGroup,
            textBtnCancel: l10n.DIALOGS.btnCancel,
          });

          await promise;
        } catch (error) {
          /** User cancelled the dialog, we treat it as non-error case, so just return */
          return null;
        }
      }

      dispatch(setSavedSearchGroupRemoving({ path, isRemoving: true }));

      await sdk.savedSearches.deleteGroup(path, isShared);

      Toast(l10n.TAGSTREE.textHasBeenRemovedGroup(groupName), { type: 'success' });

      return { userId: getState().user._id };
    } catch (error) {
      dispatch(setSavedSearchGroupRemoving({ path, isRemoving: false }));
      Logger.error(new Error('Can not delete saved search group'), { error }, [
        'DeleteSavedSearchGroupFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);

      Toast('Error deleting saved search group', { type: 'error' });

      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    setSavedSearchGroupRemoving,
    (state, { payload: { path, isRemoving } }) => {
      state.tree = setField(state.tree, path, 'isBusy', isRemoving);
    },
  ).addCase(
    deleteGroup.fulfilled,
    (state, { payload, meta: { arg: { path, isShared } } }) => {
      if (!payload) {
        /** if cancelled before delete request */
        return;
      }
      const { userId } = payload;
      const start = path + NEW_PATH_DELIMITER;
      state.all = state.all.map((ss) => {
        // Mirror the server's `^path(→|$)` regex so we also drop the empty-
        // group placeholder doc whose path equals `path` exactly. Without
        // this the placeholder lingered locally until the next refetch and
        // re-materialized the (now-deleted) group in the tree.
        const matchesGroup = ss.path && (ss.path === path || ss.path.startsWith(start));
        if (matchesGroup) {
          if (isShared && ss.teamId) return null;
          if (!isShared && !ss.teamId) return null;
        }
        return ss;
      }).filter(Boolean);
      state.tree = makeTree(
        state.all,
        userId,
        state.searchQuery,
        state.treeSortType || DEFAULT_SAVED_SEARCH_TREE_SORT,
      );
    },
  ).addCase(
    deleteGroup.rejected,
    (state, { meta: { arg: { path } }, error }) => {
      state.error = error;
      state.tree = setField(state.tree, path, 'isBusy', false);
    },
  );
};
